import { PhosphorGearAnchor } from "components/Atoms/PhosphorIconAnchor";
import { SearchInput } from "components/Atoms/SearchInput";
import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import { Loading } from "components/panels";
import Pagination from "pagination/components/pagination";
import React from "react";
import { isNullOrEmptyString } from "utilities";
import { routes } from "utilities/routing/generatedRoutes";
import { useDebounce } from "utilities/usehooks";
import { TemplateCard } from "./TemplateCard";
import { useTemplatesQuery } from "./hooks";

interface Props {
  type: server.dto.MessageTemplateTypes
  onTemplateSelected: (template: server.dto.MessageTemplate, language: server.dto.Languages) => void
  canConfigureTemplates: boolean
}

const initialInstruction = (type: server.dto.MessageTemplateTypes): server.dto.ListMessageTemplateInstruction => ({
  keyword: undefined,
  type,
  page: 1,
  perPage: 20,
});

const ChooseTemplateModal = (props: Props) => {
  const [keyword, setKeyword] = React.useState("");
  const debouncedKeyword = useDebounce(keyword);
  const [instruction, setInstruction] = React.useState<server.dto.ListMessageTemplateInstruction>(initialInstruction(props.type));

  const templatesQuery = useTemplatesQuery({
    ...instruction,
    keyword: debouncedKeyword,
  }, { keepPreviousData: true });

  const [displaySearch, setDisplaySearch] = React.useState(false);

  React.useEffect(() => {
    if (!templatesQuery.data) return;

    // If the "keyword" is not set, it means we're loading all the templates
    if (isNullOrEmptyString(templatesQuery.data.data.request.keyword)) {

      // We show the search input only if there are multiple pages in the first query result
      if (!displaySearch && templatesQuery.data.data.pagination.total > 1) {
        setDisplaySearch(true);
      }
    }
  }, [templatesQuery.data, displaySearch]);



  if (templatesQuery.isLoading) {
    return <div className="margin-bottom scrollbox-auto grow1" style={{ minHeight: 200 }}>
      <Loading />
    </div>;
  }

  const templates = templatesQuery.data.data.data;
  const configureUrl = routes.messageTemplate_List(props.type).absolute;
  const displayPagination = templatesQuery.data.data.pagination.pageCount > 1;

  return <div className="flex-column grow1 gap" style={{ minHeight: 0 }}>
    {displaySearch && <div>
      <SearchInput
        focusOnMount
        value={keyword ?? ""}
        onChange={(_, { value }) => setKeyword(value)}
        placeholder={RESX.GeneralLabels.SearchByName}
      />
    </div>}

    {displayPagination && <div className="flex justify-end">
      <Pagination
        onPaginate={(page) => setInstruction(i => ({ ...i, page }))}
        paginationData={{
          page: instruction.page,
          total: templatesQuery.data?.data.pagination.total ?? 0,
          perPage: instruction.perPage,
          maxPerPage: templatesQuery.data?.data.pagination.maxPerPage ?? 0,
          pageCount: templatesQuery.data?.data.pagination.pageCount ?? 0
        }}
        noInitialPagination
      />
    </div>}


    {templatesQuery.data.data.data.length > 0 && <div className="flex-column gap-small margin-bottom scrollbox-auto grow1" style={{ minHeight: 200 }}>
      {templates.map(template =>
        <TemplateCard template={template} key={template.messageTemplateId} onTemplateSelected={props.onTemplateSelected} />
      )}
    </div>}

    {templatesQuery.data.data.data.length === 0 && <div className="grow1"><NoDataAvailableDiv /></div>}

    {props.canConfigureTemplates &&
      <div>
        <div className="message ui blue flex margin-bottom gap" style={{ padding: 4 }}>
          <div className="grow1 padding">
            {RESX.Template.YouCanCreateYourOwnTemplates}
          </div>
          <div>
            <PhosphorGearAnchor
              href={configureUrl}
              layout="in-square"
              backgroundColor="light"
              padding="large"
            />
          </div>
        </div>
      </div>}
  </div>;
};

export default ChooseTemplateModal;